<script lang="ts">
	import { CollisionPriority } from "@dnd-kit/abstract";
	import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/svelte";
	import { isSortable } from "@dnd-kit/svelte/sortable";
	import { flip, shift, size } from "@floating-ui/dom";
	import deepEqual from "deep-equal";
	import { Dropdown, DropdownItem, Heading, Input } from "flowbite-svelte";
	import { getContext } from "svelte";
	import { FieldPropertiesSchema, FieldSchema, FieldType, type Field, type FieldProperties } from "#gen/forms/v1/field_pb.js";
	import { FormSchema, type Form, type Rule } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { move, type DragEndEvent, type DragOverEvent } from "#lib/components/flow/editor/types.js";
	import FieldContainer from "#lib/components/page/components/form/FieldContainer.svelte";
	import { fieldTypes } from "#lib/components/page/components/form/fieldTypes.js";
	import { generateId, removeFieldFromRules } from "#lib/components/page/components/form/utils.js";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import { clone, create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Client } from "@optimizely/optimizely-sdk";
	import type { Readable } from "svelte/store";

	interface Props {
		form: Form;
		onInput?: (form: Form) => void;
		onChange?: (form: Form) => void;
	}

	const flags: Client = getContext("flags");
	const claims: Readable<JwtPayload> = getContext("claims");

	let { form, onInput, onChange }: Props = $props();

	let fields = $derived(form.fields);
	let snapshot: Field[] = [];

	// Keep the field-type picker within the viewport
	const pickerFloatingProps: Record<string, unknown> = {
		strategy: "fixed",
		middlewares: [
			flip({ padding: 8 }),
			shift({ padding: 8 }),
			size({
				padding: 8,
				apply({ placement, elements }) {
					const ref = (elements.reference as Element).getBoundingClientRect();
					const room = placement.startsWith("top") ? ref.top : window.innerHeight - ref.bottom;
					Object.assign(elements.floating.style, {
						maxHeight: `${Math.max(0, Math.min(room, window.innerHeight) - 16)}px`,
						overflowY: "auto",
					});
				},
			}),
		],
	};

	let pendingScroll: Field | undefined = $state();
	let open = $state(false);

	$effect(() => {
		if (!pendingScroll) {
			return;
		}
		const element = document.getElementById(`field-editor-${pendingScroll.name}`);
		element?.scrollIntoView({ behavior: "smooth", block: "start" });
		pendingScroll = undefined;
	});

	// Always build a new form object so the reference changes and parent reactivity fires.
	function buildNextForm(fields: Field[], rules: Rule[]): Form {
		const next = clone(FormSchema, form);
		next.fields = fields;
		next.rules = rules;
		return next;
	}

	function updateFields(fields: Field[], rules: Rule[]) {
		onChange?.(buildNextForm(fields, rules));
	}

	function selectElement(title: string, type: FieldType, properties?: FieldProperties) {
		addField(title, type, properties);
		open = false;
	}

	function addField(title: string, fieldType: FieldType, properties?: FieldProperties) {
		const name = generateId(form.fields, FieldType[fieldType].toLowerCase());
		const newField = create(FieldSchema, {
			name: name,
			title: title,
			fieldType: fieldType,
			properties: clone(FieldPropertiesSchema, properties),
			active: true,
			required: false,
		});
		updateFields([...form.fields, newField], form.rules);
		pendingScroll = newField;
	}

	function onDuplicate(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex < 0) {
			return;
		}
		const newField = clone(FieldSchema, form.fields[fieldIndex]);
		newField.id = "";
		newField.immutable = false;
		newField.name = generateId(form.fields, newField.name);
		updateFields([...form.fields.slice(0, fieldIndex + 1), newField, ...form.fields.slice(fieldIndex + 1)], form.rules);
		pendingScroll = newField;
	}

	function onRemove(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex < 0) {
			return;
		}
		const newFields = form.fields.filter((_, i) => i !== fieldIndex);
		updateFields(newFields, removeFieldFromRules(form.rules, fieldName));
	}

	function onMoveUp(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex <= 0) {
			return;
		}
		const field = form.fields[fieldIndex];
		updateFields(move(form.fields, fieldIndex, fieldIndex - 1), form.rules);
		pendingScroll = field;
	}

	function onMoveDown(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex < 0 || fieldIndex >= form.fields.length - 1) {
			return;
		}
		const field = form.fields[fieldIndex];
		updateFields(move(form.fields, fieldIndex, fieldIndex + 1), form.rules);
		pendingScroll = field;
	}

	function onDragStart() {
		snapshot = fields.slice();
	}

	function onDragOver(event: DragOverEvent) {
		const { source, target } = event.operation;
		if (isSortable(source) && isSortable(target)) {
			const fromIndex = source.index;
			const toIndex = target.index;
			if (fromIndex !== toIndex) {
				const newFields = [...fields];
				const [removed] = newFields.splice(fromIndex, 1);
				newFields.splice(toIndex, 0, removed);
				fields = newFields;
			}
		}
	}

	function onDragEnd(event: DragEndEvent) {
		if (event.canceled || deepEqual(fields, snapshot)) {
			fields = snapshot;
			return;
		}
		updateFields(fields, form.rules);
	}

	function handleOnInput(field: Field) {
		const fieldIndex = form.fields.findIndex(f => f.name == field.name);
		if (fieldIndex < 0) {
			return;
		}
		onInput?.(
			buildNextForm(
				form.fields.map((f, i) => (i === fieldIndex ? field : f)),
				form.rules,
			),
		);
	}

	function handleOnChange(field: Field) {
		const fieldIndex = form.fields.findIndex(f => f.name == field.name);
		if (fieldIndex < 0) {
			return;
		}
		onChange?.(
			buildNextForm(
				form.fields.map((f, i) => (i === fieldIndex ? field : f)),
				form.rules,
			),
		);
	}

	function handleActionTextInput(e: Event) {
		const next = clone(FormSchema, form);
		const value = (e.target as HTMLInputElement).value;
		next.actionText = value.length > 0 ? value : undefined;
		onInput?.(next);
	}

	function handleSettingsChange() {
		onChange?.(clone(FormSchema, form));
	}
</script>

<div class="flex flex-col gap-6">
	<div class="flex flex-col gap-4">
		<P class="text-sm font-bold text-black">Form fields</P>
		{#if form.fields.length === 0}
			<div class="rounded-lg border border-dashed p-8">
				<P size="sm" weight="semibold" align="center">Add fields to start building your form.</P>
				<P size="sm" align="center" secondary>Your form fields will show up here. Use the "Add field" button below to add one.</P>
			</div>
		{:else}
			<DragDropProvider sensors={[KeyboardSensor, PointerSensor]} {onDragStart} {onDragOver} {onDragEnd}>
				<FieldContainer id={form.id} collisionPriority={CollisionPriority.Low} {form} {fields} onInput={handleOnInput} onChange={handleOnChange} {onRemove} {onDuplicate} {onMoveUp} {onMoveDown} />
			</DragDropProvider>
		{/if}
		<div>
			<Button id="form-content-add-field-button" color="alternative" data-action-name="form-content-add-field" data-action-title="Add field">
				Add field
				<Icon name="plus" class="ml-2 h-4 w-4" />
			</Button>
			<Dropdown triggeredBy="#form-content-add-field-button" bind:open arrow={false} placement="top-start" class="w-145 rounded-lg border border-gray-300" {...pickerFloatingProps}>
				<div class="grid grid-cols-3 gap-4 p-4">
					{#each fieldTypes as section (section.title)}
						<div>
							<Heading tag="h3" color="text-gray-500" class="p-3 text-xs font-normal uppercase">{section.title}</Heading>
							{#each section.elements as element (element.title)}
								{#if !element.requiredFeatureFlag || isFeatureEnabled(flags, element.requiredFeatureFlag, $claims)}
									<DropdownItem class="px-2 py-2 hover:rounded-lg" on:click={() => selectElement(element.title, element.fieldType, element.fieldProperties)} data-action-name="form-content-add-field-type" data-action-title={element.title}>
										<div class="flex items-center gap-2">
											<div class="rounded-lg p-2 text-center" style="background-color: {section.accentColor};">
												<Icon name={element.icon} iconSet={element.iconSet} class="m-auto h-5 w-5" />
											</div>
											<P size="sm" weight="medium">{element.title}</P>
										</div>
									</DropdownItem>
								{/if}
							{/each}
						</div>
					{/each}
				</div>
			</Dropdown>
		</div>
	</div>

	<div class="flex flex-col gap-2">
		<P class="text-sm font-bold text-black">Submit button</P>
		<div class="border-l-[5px] border-l-gray-300 pl-6">
			<label for="actionText" class="mb-2 inline-block text-sm font-medium">Button text</label>
			<Input type="text" id="actionText" placeholder="Submit" value={form.actionText} on:input={handleActionTextInput} on:change={handleSettingsChange} />
		</div>
	</div>
</div>
